/*
 * Loom & Wall — Favorites
 * ------------------------------------------------------------------
 * Stores a customer's favorited products.
 *  - Logged-in customer + Supabase configured  -> synced to Supabase (cross-device)
 *  - Otherwise                                  -> per-browser localStorage fallback
 *
 * The UI (heart buttons, favorites grid) is identical for both backends, so the
 * storage layer can be upgraded later (e.g. signed edge function) without UI changes.
 *
 * Public API: window.LWFavorites
 *   .has(id) -> boolean
 *   .toggle(id, handle) -> Promise
 *   .list() -> [{ id, handle }]
 *   .whenReady() -> Promise
 * Emits `lw:favorites-changed` on document with detail { ids: [...] }.
 */
(function () {
  const CFG = window.LW_FAV_CONFIG || {};
  const SUPA_URL = (CFG.supabaseUrl || '').replace(/\/+$/, '');
  const SUPA_KEY = CFG.supabaseAnonKey || '';
  const CUSTOMER_ID = CFG.customerId ? String(CFG.customerId) : null;
  const TABLE = 'favorites';
  const LS_KEY = 'lw_favorites';

  const HAS_SUPABASE = Boolean(SUPA_URL && SUPA_KEY);
  const USE_REMOTE = HAS_SUPABASE && Boolean(CUSTOMER_ID);

  const idSet = new Set(); // product ids as strings
  const handleMap = new Map(); // id -> handle
  let ready = false;
  let readyResolve;
  const readyPromise = new Promise((res) => (readyResolve = res));

  /* ---------- helpers ---------- */
  function emit() {
    document.dispatchEvent(
      new CustomEvent('lw:favorites-changed', { detail: { ids: [...idSet] } })
    );
  }

  function lsRead() {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function lsWrite() {
    try {
      const arr = [...handleMap.entries()].map(([id, handle]) => ({ id, handle }));
      localStorage.setItem(LS_KEY, JSON.stringify(arr));
    } catch (e) {
      /* ignore quota errors */
    }
  }

  function supaHeaders(extra) {
    return Object.assign(
      {
        apikey: SUPA_KEY,
        Authorization: 'Bearer ' + SUPA_KEY,
        'Content-Type': 'application/json',
      },
      extra || {}
    );
  }

  /* ---------- remote (Supabase REST) ---------- */
  async function remoteList() {
    const url =
      SUPA_URL +
      '/rest/v1/' +
      TABLE +
      '?shopify_customer_id=eq.' +
      encodeURIComponent(CUSTOMER_ID) +
      '&select=product_id,product_handle';
    const res = await fetch(url, { headers: supaHeaders() });
    if (!res.ok) throw new Error('Favorites list failed: ' + res.status);
    return res.json();
  }

  async function remoteAdd(id, handle) {
    const url = SUPA_URL + '/rest/v1/' + TABLE;
    const res = await fetch(url, {
      method: 'POST',
      headers: supaHeaders({ Prefer: 'resolution=merge-duplicates,return=minimal' }),
      body: JSON.stringify({
        shopify_customer_id: CUSTOMER_ID,
        product_id: String(id),
        product_handle: handle || null,
      }),
    });
    if (!res.ok) throw new Error('Favorite add failed: ' + res.status);
  }

  async function remoteRemove(id) {
    const url =
      SUPA_URL +
      '/rest/v1/' +
      TABLE +
      '?shopify_customer_id=eq.' +
      encodeURIComponent(CUSTOMER_ID) +
      '&product_id=eq.' +
      encodeURIComponent(String(id));
    const res = await fetch(url, { method: 'DELETE', headers: supaHeaders() });
    if (!res.ok) throw new Error('Favorite remove failed: ' + res.status);
  }

  /* ---------- init ---------- */
  function loadLocalInto() {
    lsRead().forEach((it) => {
      const id = String(it.id);
      idSet.add(id);
      handleMap.set(id, it.handle || null);
    });
  }

  async function init() {
    if (USE_REMOTE) {
      try {
        const local = lsRead();
        const remote = await remoteList();
        remote.forEach((r) => {
          const id = String(r.product_id);
          idSet.add(id);
          handleMap.set(id, r.product_handle || null);
        });
        // Merge favorites saved while logged out, then clear local cache.
        for (const it of local) {
          const id = String(it.id);
          if (!idSet.has(id)) {
            try {
              await remoteAdd(id, it.handle);
            } catch (e) {
              /* skip individual failures */
            }
            idSet.add(id);
            handleMap.set(id, it.handle || null);
          }
        }
        if (local.length) {
          try {
            localStorage.removeItem(LS_KEY);
          } catch (e) {}
        }
      } catch (e) {
        console.warn('[LWFavorites] Supabase unavailable, using local storage.', e);
        idSet.clear();
        handleMap.clear();
        loadLocalInto();
      }
    } else {
      loadLocalInto();
    }

    ready = true;
    readyResolve();
    syncButtons();
    emit();
  }

  /* ---------- mutations ---------- */
  async function toggle(id, handle) {
    id = String(id);
    const wasFav = idSet.has(id);

    // Optimistic update
    if (wasFav) {
      idSet.delete(id);
      handleMap.delete(id);
    } else {
      idSet.add(id);
      handleMap.set(id, handle || null);
    }
    syncButtons();
    emit();

    try {
      if (USE_REMOTE) {
        if (wasFav) await remoteRemove(id);
        else await remoteAdd(id, handle);
      } else {
        lsWrite();
      }
    } catch (e) {
      // Roll back on failure
      console.warn('[LWFavorites] toggle failed, reverting.', e);
      if (wasFav) {
        idSet.add(id);
        handleMap.set(id, handle || null);
      } else {
        idSet.delete(id);
        handleMap.delete(id);
      }
      syncButtons();
      emit();
    }
  }

  /* ---------- button wiring ---------- */
  function syncButtons(root) {
    (root || document).querySelectorAll('.lw-fav-btn').forEach((btn) => {
      const id = btn.getAttribute('data-product-id');
      if (!id) return;
      const isFav = idSet.has(String(id));
      btn.setAttribute('aria-pressed', isFav ? 'true' : 'false');
      btn.classList.toggle('is-favorited', isFav);
    });
  }

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.lw-fav-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const id = btn.getAttribute('data-product-id');
    const handle = btn.getAttribute('data-product-handle');
    if (id) toggle(id, handle);
  });

  // Re-sync when new cards are injected (e.g. quick view, section rendering)
  document.addEventListener('lw:favorites-changed', function () {
    /* no-op; buttons already synced in syncButtons */
  });

  /* ---------- public API ---------- */
  window.LWFavorites = {
    has: (id) => idSet.has(String(id)),
    toggle,
    list: () => [...handleMap.entries()].map(([id, handle]) => ({ id, handle })),
    isReady: () => ready,
    whenReady: () => readyPromise,
    syncButtons,
    config: { hasSupabase: HAS_SUPABASE, useRemote: USE_REMOTE, loggedIn: Boolean(CUSTOMER_ID) },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ------------------------------------------------------------------
 * <lw-favorites-grid> — renders the customer's favorited products.
 * ---------------------------------------------------------------- */
class LWFavoritesGrid extends HTMLElement {
  connectedCallback() {
    this.gridEl = this.querySelector('[data-favorites-grid]');
    this.emptyEl = this.querySelector('[data-favorites-empty]');
    this.loadingEl = this.querySelector('[data-favorites-loading]');
    this._onChange = () => this.render();
    document.addEventListener('lw:favorites-changed', this._onChange);
    window.LWFavorites.whenReady().then(() => this.render());
  }

  disconnectedCallback() {
    document.removeEventListener('lw:favorites-changed', this._onChange);
  }

  async render() {
    const items = window.LWFavorites.list().filter((it) => it.handle);
    if (this.loadingEl) this.loadingEl.hidden = true;

    if (!items.length) {
      if (this.gridEl) this.gridEl.innerHTML = '';
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }
    if (this.emptyEl) this.emptyEl.hidden = true;

    const cards = await Promise.all(
      items.map((it) =>
        fetch('/products/' + it.handle + '.js')
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );

    this.gridEl.innerHTML = cards
      .filter(Boolean)
      .map((p) => this.cardHTML(p))
      .join('');
    window.LWFavorites.syncButtons(this.gridEl);
  }

  cardHTML(p) {
    const img = p.featured_image
      ? `<img src="${p.featured_image}&width=533" alt="${this.esc(p.title)}" loading="lazy" width="533">`
      : '';
    const price = this.formatMoney(p.price);
    const compare =
      p.compare_at_price && p.compare_at_price > p.price
        ? `<s class="lw-fav-card__compare">${this.formatMoney(p.compare_at_price)}</s>`
        : '';
    return `
      <div class="lw-fav-card">
        <a class="lw-fav-card__media" href="${p.url}">${img}</a>
        <button type="button" class="lw-fav-btn lw-fav-btn--card is-favorited"
          data-product-id="${p.id}" data-product-handle="${p.handle}"
          aria-pressed="true" aria-label="Remove ${this.esc(p.title)} from favorites" title="Remove from favorites">
          <span class="lw-fav-btn__icon" aria-hidden="true">${LWFavoritesGrid.heartSVG}</span>
        </button>
        <div class="lw-fav-card__info">
          <a class="lw-fav-card__title" href="${p.url}">${this.esc(p.title)}</a>
          <div class="lw-fav-card__price">${compare} <span>${price}</span></div>
        </div>
      </div>`;
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  esc(str) {
    return String(str || '').replace(/[&<>"']/g, (c) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
}

LWFavoritesGrid.heartSVG =
  '<svg viewBox="0 0 24 24" width="20" height="20"><path d="M12 21s-7.5-4.9-10-9.2C.6 9 1.6 5.6 4.7 4.7c2-.6 4 .2 5.1 1.9L12 8.9l2.2-2.3c1.1-1.7 3.1-2.5 5.1-1.9 3.1.9 4.1 4.3 2.7 7.1C19.5 16.1 12 21 12 21z"/></svg>';

if (!customElements.get('lw-favorites-grid')) {
  customElements.define('lw-favorites-grid', LWFavoritesGrid);
}
